import log from '@/lib/logger';
import { useState, useMemo, useEffect } from 'react'
import { CheckIcon, InformationCircleIcon, ExclamationTriangleIcon, XMarkIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, NoSymbolIcon, ArrowUturnLeftIcon } from '@heroicons/react/16/solid'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/data-table'
import { Button } from '@/components/tailwind/button'
import { Text } from '@/components/tailwind/text'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '@/components/tailwind/dialog'
import { Field, Label } from '@/components/tailwind/fieldset'
import { Textarea } from '@/components/tailwind/textarea'
import { Input, InputGroup } from '@/components/tailwind/input'
import { useDebounce } from '@/hooks/use-debounce'
import type { BillingRunItem, SepaStatus } from '@/services/billing-services'
import { updateBillingItemPaymentStatus, bulkUpdateBillingItemsPaymentStatus, downloadSepaXml, getSepaStatus } from '@/services/billing-services'
import { billingItemColumns } from './billing-item-columns'
import { useBillingTranslation } from '@/i18n/hooks'
import { useAuth } from '@/context/use-auth'
import { formatDateString } from '@/lib/utils'

interface BillingRunItemTableEnhancedProps {
  items: BillingRunItem[]
  loading?: boolean
  onItemsUpdated?: (items: BillingRunItem[]) => void
  billingRunDate?: string
  billingRunId?: string
  sepaXmlGeneratedAt?: string
  onSepaXmlDownloaded?: () => void
}

type FilterType = 'all' | 'sepa' | 'manual' | 'paid' | 'failed' | 'cancelled'

const MAX_CANCELLATION_REASON_LENGTH = 500

function getDefaultExecutionDate(): string {
  const date = new Date()
  let businessDays = 0
  while (businessDays < 3) {
    date.setDate(date.getDate() + 1)
    const dayOfWeek = date.getDay()
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++
    }
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function BillingRunItemTableEnhanced({ 
  items, 
  loading = false, 
  onItemsUpdated,
  billingRunDate,
  billingRunId,
  sepaXmlGeneratedAt,
  onSepaXmlDownloaded
}: BillingRunItemTableEnhancedProps) {
  const [filter, setFilter] = useState<FilterType>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set())
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const [sepaDialogOpen, setSepaDialogOpen] = useState(false)
  const [markAllPaidDialogOpen, setMarkAllPaidDialogOpen] = useState(false)
  const [executionDate, setExecutionDate] = useState(() => getDefaultExecutionDate())
  const [downloadingSepa, setDownloadingSepa] = useState(false)
  const [sepaError, setSepaError] = useState<string | null>(null)
  const [sepaStatus, setSepaStatus] = useState<SepaStatus>({ available: true, reason: null })
  const [itemToCancel, setItemToCancel] = useState<BillingRunItem | null>(null)
  const [itemToRevert, setItemToRevert] = useState<BillingRunItem | null>(null)
  const [cancellationReason, setCancellationReason] = useState('')
  const { t } = useBillingTranslation()
  const { token } = useAuth()
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  useEffect(() => {
    if (!token) return
    getSepaStatus(token).then(setSepaStatus)
  }, [token])

  const businessDaysSinceBilling = useMemo(() => {
    if (!billingRunDate) return 0
    
    const start = new Date(billingRunDate)
    const today = new Date()
    let businessDays = 0
    
    const current = new Date(start)
    while (current <= today) {
      const dayOfWeek = current.getDay()
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++
      }
      current.setDate(current.getDate() + 1)
    }
    
    return businessDays
  }, [billingRunDate])

  const showSepaReminder = businessDaysSinceBilling >= 3

  const cancelledCount = useMemo(
    () => items.filter(item => item.paymentStatus === 'CANCELLED').length,
    [items]
  )

  // The cancelled filter button disappears with its last item.
  useEffect(() => {
    if (filter === 'cancelled' && cancelledCount === 0) {
      setFilter('all')
    }
  }, [filter, cancelledCount])

  const filteredItems = useMemo(() => {
    const byFilter = (() => {
      switch (filter) {
        case 'sepa':
          return items.filter(item => item.paymentMethod === 'SEPA')
        case 'manual':
          return items.filter(item => item.paymentMethod === 'MANUAL')
        case 'paid':
          return items.filter(item => item.paymentStatus === 'PAID')
        case 'failed':
          return items.filter(item => item.paymentStatus === 'FAILED')
        case 'cancelled':
          return items.filter(item => item.paymentStatus === 'CANCELLED')
        default:
          return items
      }
    })()

    const search = debouncedSearchTerm.trim().toLowerCase()
    if (!search) return byFilter

    return byFilter.filter(item =>
      item.memberName.toLowerCase().includes(search) ||
      item.memberNumber.toLowerCase().includes(search)
    )
  }, [items, filter, debouncedSearchTerm])

  const stats = useMemo(() => {
    return {
      total: items.length,
      sepa: items.filter(item => item.paymentMethod === 'SEPA').length,
      manual: items.filter(item => item.paymentMethod === 'MANUAL').length,
      paid: items.filter(item => item.paymentStatus === 'PAID').length,
      failed: items.filter(item => item.paymentStatus === 'FAILED').length,
      cancelled: cancelledCount,
      sepaPending: items.filter(item =>
        item.paymentMethod === 'SEPA' && item.paymentStatus === 'PENDING'
      ).length
    }
  }, [items, cancelledCount])

  // Written-off items drop out of the denominator instead of counting as done.
  const paymentProgress = useMemo(() => {
    const open = items.filter(item => item.amount > 0 && item.paymentStatus !== 'CANCELLED')
    const paid = open.filter(item => item.paymentStatus === 'PAID').length
    return open.length > 0 ? Math.round((paid / open.length) * 100) : 0
  }, [items])

  async function handleUpdatePaymentStatus(itemId: string, newStatus: string, reason?: string) {
    if (!token) return

    setUpdatingItems(prev => new Set(prev).add(itemId))

    try {
      const result = await updateBillingItemPaymentStatus(token, itemId, {
        paymentStatus: newStatus,
        ...(reason ? { cancellationReason: reason } : {})
      })

      if (result && onItemsUpdated) {
        const updatedItems = items.map(item => 
          item.id === itemId ? result : item
        )
        onItemsUpdated(updatedItems)
      }
    } catch (error) {
      log.error('Error updating payment status:', error)
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev)
        newSet.delete(itemId)
        return newSet
      })
    }
  }

  function openCancelDialog(item: BillingRunItem) {
    setCancellationReason('')
    setItemToCancel(item)
  }

  async function handleConfirmCancellation() {
    const item = itemToCancel
    if (!item) return

    setItemToCancel(null)
    await handleUpdatePaymentStatus(item.id, 'CANCELLED', cancellationReason.trim() || undefined)
    setCancellationReason('')
  }

  async function handleConfirmRevertCancellation() {
    const item = itemToRevert
    if (!item) return

    setItemToRevert(null)
    await handleUpdatePaymentStatus(item.id, 'PENDING')
  }

  async function handleMarkAllSepaPaid() {
    if (!token) return

    const sepaItems = items.filter(item =>
      item.paymentMethod === 'SEPA' && item.paymentStatus === 'PENDING'
    )

    if (sepaItems.length === 0) return

    setMarkAllPaidDialogOpen(false)
    setBulkUpdating(true)
    
    try {
      const results = await bulkUpdateBillingItemsPaymentStatus(token, {
        itemIds: sepaItems.map(item => item.id),
        paymentStatus: 'PAID'
      })

      if (results && onItemsUpdated) {
        const resultMap = new Map(results.map(item => [item.id, item]))
        const updatedItems = items.map(item => 
          resultMap.get(item.id) || item
        )
        onItemsUpdated(updatedItems)
      }
    } catch (error) {
      log.error('Error marking SEPA items as paid:', error)
    } finally {
      setBulkUpdating(false)
    }
  }

  async function handleDownloadSepaXml() {
    if (!token || !billingRunId) return

    setDownloadingSepa(true)
    setSepaError(null)

    try {
      const blob = await downloadSepaXml(token, billingRunId, executionDate)
      
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sepa-lastschrift-${executionDate}.xml`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      setSepaDialogOpen(false)
      onSepaXmlDownloaded?.()
    } catch (error) {
      if (error instanceof Error && error.message === 'SEPA_CONFIG_MISSING') {
        setSepaError(t('errors.sepaConfigMissing'))
      } else {
        setSepaError(t('errors.sepaXmlFailed'))
      }
    } finally {
      setDownloadingSepa(false)
    }
  }

  const actionsColumn: ColumnDef<BillingRunItem> = {
    id: 'actions',
    header: () => t('table.headers.actions'),
    cell: ({ row }) => {
      const item = row.original
      const isCancelled = item.paymentStatus === 'CANCELLED'

      return (
        <div className="flex gap-1">
          {item.amount > 0 && isCancelled && (
            <Button
              plain
              onClick={() => setItemToRevert(item)}
              disabled={updatingItems.has(item.id)}
              title={t('actions.revertCancellation')}
            >
              <ArrowUturnLeftIcon className="h-4 w-4 text-blue-600" />
            </Button>
          )}
          {item.amount > 0 && !isCancelled && (
            <>
              {item.paymentStatus !== 'PAID' && (
                <Button
                  plain
                  onClick={() => handleUpdatePaymentStatus(item.id, 'PAID')}
                  disabled={updatingItems.has(item.id)}
                  title={t('actions.markAsPaid')}
                >
                  <CheckIcon className="h-4 w-4 text-green-600" />
                </Button>
              )}
              {item.paymentStatus !== 'FAILED' && (
                <Button
                  plain
                  onClick={() => handleUpdatePaymentStatus(item.id, 'FAILED')}
                  disabled={updatingItems.has(item.id)}
                  title={t('actions.markAsFailed')}
                >
                  <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                </Button>
              )}
              {item.paymentStatus !== 'PENDING' && (
                <Button
                  plain
                  onClick={() => handleUpdatePaymentStatus(item.id, 'PENDING')}
                  disabled={updatingItems.has(item.id)}
                  title={t('actions.markAsPending')}
                >
                  <XMarkIcon className="h-4 w-4 text-yellow-600" />
                </Button>
              )}
              {item.paymentStatus !== 'PAID' && (
                <Button
                  plain
                  onClick={() => openCancelDialog(item)}
                  disabled={updatingItems.has(item.id)}
                  title={t('actions.markAsCancelled')}
                >
                  <NoSymbolIcon className="h-4 w-4 text-gray-500" />
                </Button>
              )}
            </>
          )}
          {item.cancellationReason && (
            <Text className="text-xs text-gray-500 ml-2 max-w-40 truncate" title={item.cancellationReason}>
              {item.cancellationReason}
            </Text>
          )}
          {item.paidAt && (
            <Text className="text-xs text-gray-500 ml-2">
              {formatDateString(item.paidAt)}
            </Text>
          )}
        </div>
      )
    },
  }

  const columns: ColumnDef<BillingRunItem>[] = [...billingItemColumns, actionsColumn]

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-sm text-gray-500">{t('table.loading')}</div>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-gray-500">{t('table.empty')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Progress Bar */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-2">
          <Text className="text-sm font-medium">{t('progress.paymentStatus')}</Text>
          <Text className="text-sm text-gray-500">
            {stats.paid} / {stats.total - stats.cancelled} {t('progress.paid')}
          </Text>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className={`${paymentProgress === 100 ? 'bg-green-600' : 'bg-blue-500'} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${paymentProgress}%` }}
          />
        </div>
      </div>

      {/* SEPA Actions */}
      {(stats.sepaPending > 0 || stats.sepa > 0) && billingRunId && (
        <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-r-lg">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-blue-400 dark:text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <Text className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                {t('info.sepaTitle')}
              </Text>
              {!sepaStatus.available && (
                <Text className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {t(`sepaStatus.${sepaStatus.reason ?? 'unavailable'}`)}
                </Text>
              )}
              <Text className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                {stats.sepaPending > 0 && showSepaReminder
                  ? t('info.sepaReminder.message', { days: businessDaysSinceBilling, count: stats.sepaPending })
                  : stats.sepaPending > 0
                    ? t('info.sepaPending', { count: stats.sepaPending })
                    : t('info.sepaAllProcessed')
                }
                {sepaXmlGeneratedAt && (
                  <span className="block mt-1 text-xs">
                    {t('info.sepaXmlGeneratedAt', { date: formatDateString(sepaXmlGeneratedAt) })}
                  </span>
                )}
              </Text>
              <div className="flex gap-2 mt-3">
                <Button
                  color="blue"
                  onClick={() => setSepaDialogOpen(true)}
                  disabled={!sepaStatus.available || stats.sepaPending === 0}
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                  {t('actions.downloadSepaXml')}
                </Button>
                {stats.sepaPending > 0 && sepaXmlGeneratedAt && (
                  <Button
                    outline
                    onClick={() => setMarkAllPaidDialogOpen(true)}
                    disabled={bulkUpdating}
                  >
                    {t('actions.markAllSepaPaid')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and search */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {filter === 'all' ? (
            <Button onClick={() => setFilter('all')}>
              {t('filter.all')} ({stats.total})
            </Button>
          ) : (
            <Button plain onClick={() => setFilter('all')}>
              {t('filter.all')} ({stats.total})
            </Button>
          )}
          {filter === 'sepa' ? (
            <Button onClick={() => setFilter('sepa')}>
              {t('filter.sepa')} ({stats.sepa})
            </Button>
          ) : (
            <Button plain onClick={() => setFilter('sepa')}>
              {t('filter.sepa')} ({stats.sepa})
            </Button>
          )}
          {filter === 'manual' ? (
            <Button onClick={() => setFilter('manual')}>
              {t('filter.manual')} ({stats.manual})
            </Button>
          ) : (
            <Button plain onClick={() => setFilter('manual')}>
              {t('filter.manual')} ({stats.manual})
            </Button>
          )}
          {filter === 'paid' ? (
            <Button onClick={() => setFilter('paid')}>
              {t('filter.paid')} ({stats.paid})
            </Button>
          ) : (
            <Button plain onClick={() => setFilter('paid')}>
              {t('filter.paid')} ({stats.paid})
            </Button>
          )}
          {filter === 'failed' ? (
            <Button onClick={() => setFilter('failed')}>
              {t('filter.failed')} ({stats.failed})
            </Button>
          ) : (
            <Button plain onClick={() => setFilter('failed')}>
              {t('filter.failed')} ({stats.failed})
            </Button>
          )}
          {stats.cancelled > 0 && (
            filter === 'cancelled' ? (
              <Button onClick={() => setFilter('cancelled')}>
                {t('filter.cancelled')} ({stats.cancelled})
              </Button>
            ) : (
              <Button plain onClick={() => setFilter('cancelled')}>
                {t('filter.cancelled')} ({stats.cancelled})
              </Button>
            )
          )}
        </div>

        <div className="sm:w-72">
          <InputGroup>
            <MagnifyingGlassIcon />
            <Input
              type="text"
              placeholder={t('search.placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label={t('search.ariaLabel')}
              data-testid="billing-item-search"
            />
          </InputGroup>
        </div>
      </div>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={filteredItems}
        resetPageKey={`${filter}|${debouncedSearchTerm}`}
        data-testid="billing-run-items-table"
      />

      {/* Cancel Item Dialog */}
      <Dialog open={itemToCancel !== null} onClose={() => setItemToCancel(null)}>
        <DialogTitle>{t('dialog.cancelItem.title')}</DialogTitle>
        <DialogDescription>
          {t('dialog.cancelItem.description')}
        </DialogDescription>
        <DialogBody>
          <Field>
            <Label>{t('dialog.cancelItem.reasonLabel')}</Label>
            <Textarea
              rows={3}
              maxLength={MAX_CANCELLATION_REASON_LENGTH}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder={t('dialog.cancelItem.reasonPlaceholder')}
              data-testid="cancellation-reason"
            />
          </Field>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setItemToCancel(null)}>
            {t('dialog.cancelItem.cancel')}
          </Button>
          <Button color="red" onClick={handleConfirmCancellation} data-testid="confirm-cancellation">
            {t('dialog.cancelItem.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert Cancellation Dialog */}
      <Dialog open={itemToRevert !== null} onClose={() => setItemToRevert(null)}>
        <DialogTitle>{t('dialog.revertCancellation.title')}</DialogTitle>
        <DialogDescription>
          {t('dialog.revertCancellation.description')}
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setItemToRevert(null)}>
            {t('dialog.revertCancellation.cancel')}
          </Button>
          <Button color="blue" onClick={handleConfirmRevertCancellation} data-testid="confirm-revert-cancellation">
            {t('dialog.revertCancellation.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SEPA XML Dialog */}
      <Dialog open={sepaDialogOpen} onClose={() => setSepaDialogOpen(false)}>
        <DialogTitle>{t('dialog.sepaXml.title')}</DialogTitle>
        <DialogDescription>
          {t('dialog.sepaXml.description')}
        </DialogDescription>
        <DialogBody>
          <Field>
            <Label>{t('dialog.sepaXml.executionDate')}</Label>
            <Input
              type="date"
              value={executionDate}
              onChange={(e) => setExecutionDate(e.target.value)}
              min={(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` })()}
            />
          </Field>
          {sepaError && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
              <Text className="text-sm text-red-700 dark:text-red-300">{sepaError}</Text>
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setSepaDialogOpen(false)}>
            {t('dialog.sepaXml.cancel')}
          </Button>
          <Button
            color="blue"
            onClick={handleDownloadSepaXml}
            disabled={downloadingSepa || !executionDate}
          >
            {downloadingSepa ? t('actions.downloadingSepaXml') : t('dialog.sepaXml.download')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark All SEPA Paid Dialog */}
      <Dialog open={markAllPaidDialogOpen} onClose={() => setMarkAllPaidDialogOpen(false)}>
        <DialogTitle>{t('dialog.markAllSepaPaid.title')}</DialogTitle>
        <DialogDescription>
          {t('dialog.markAllSepaPaid.description', { count: stats.sepaPending })}
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setMarkAllPaidDialogOpen(false)}>
            {t('dialog.markAllSepaPaid.cancel')}
          </Button>
          <Button
            color="blue"
            onClick={handleMarkAllSepaPaid}
            disabled={bulkUpdating}
          >
            {t('dialog.markAllSepaPaid.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
